import {
  collection,
  onSnapshot,
  orderBy,
  query,
  type DocumentData,
  type Unsubscribe,
} from 'firebase/firestore';
import { useEffect, useState } from 'react';
import {
  POST_TYPE_FIELD_KEY,
  type PostAiAnalysis,
  type PostVisibility,
} from '@citizen-walk/shared-types';
import { db } from '../../lib/firebase';

/**
 * Card-print 用 posts subscribe hook (admin 第 21 弾、Phase 5 A-S6b / GAP-A8)。
 *
 * 旧 `old/apps/admin/src/features/cardPrint/postsRepo.ts::fetchPostsForCardPrint` の L2 踏襲。
 *
 * 踏襲した点:
 *   - 地区配下 posts を `createdAt desc` で list
 *   - photoThumbPath / photoPath / extraFields / aiAnalysis / authorNickname / title / comment /
 *     location / agreeCount を expose (location/agreeCount は old2 組み込み field parity 用)
 *
 * 変えた点:
 *   - 旧は once read (getDocs)、本 hook は usePostsModeration と整合させ onSnapshot (live update)
 *   - 旧の `resolvedThumbUrl` / `resolvedPhotoUrl` (Storage download URL 取得) は本 sprint scope 外。
 *     Storage URL 解決は admin app 内 preview としては `photoThumbPath` の文字列パスをそのまま
 *     文字列として渡す。実 img preview は後続 sprint で `getDownloadURL` を呼ぶ別 hook を被せる。
 *
 * 廃止した点:
 *   - 旧 v1 にあった deletedAt 論理削除 filter (v2 は archivedAt 統一)
 *
 * 認可: firestore.rules の posts read は district admin / master / 同地区認証ユーザー全員。
 *   本 page 側で canPrint guard、callable は無し (read のみ)。
 *
 * フィルタ条件 (印刷対象外):
 *   - `visibility === 'hidden_all'` の post (admin 表示はするが印刷対象外)
 *   - `archivedAt` 非 null の post (アーカイブ済)
 */

export interface CardPrintPost {
  id: string;
  authorUid: string;
  authorNickname: string;
  title: string;
  comment: string | null;
  visibility: PostVisibility;
  createdAt: Date | null;
  photoPath: string | null;
  photoThumbPath: string | null;
  /**
   * 投稿 doc の写真の URL (docs/106・docs/28)。ぼかしの作り直しではパスが同じで URL だけ変わるので、
   * 印刷用の写真はこの URL の変化で取り直す。旧投稿は null。
   */
  photoThumbUrl?: string | null;
  photoUrl?: string | null;
  aiAnalysis: PostAiAnalysis | null;
  extraFields: Record<string, unknown>;
  archivedAt: Date | null;
  /**
   * 投稿座標 (緯度経度)。card-print の組み込み field `location` で使用 (old2 parity)。
   * GeoPoint が無い旧 doc / 不正値は null。
   */
  location: { latitude: number; longitude: number } | null;
  /** 共感数 (old2 `agreeCount` field parity)。未集計は 0。 */
  agreeCount: number;
  /**
   * 所属まち歩きセッション ID (未所属は null)。
   * 左フィルタの「セッション別絞り込み」とカードの組み込み field `sessionName`
   * (sessionNameMap で名前解決) で使用。
   */
  sessionId: string | null;
}

export type CardPrintPostsState =
  | { status: 'loading'; items: []; error: null }
  | { status: 'ready'; items: CardPrintPost[]; error: null }
  | { status: 'error'; items: []; error: Error };

function mapPost(snap: { id: string; data: () => DocumentData }): CardPrintPost {
  const data = snap.data();
  const createdRaw = data.createdAt;
  const createdAt =
    createdRaw &&
    typeof createdRaw === 'object' &&
    typeof (createdRaw as { toDate?: unknown }).toDate === 'function'
      ? (createdRaw as { toDate: () => Date }).toDate()
      : null;
  const archivedRaw = data.archivedAt;
  const archivedAt =
    archivedRaw &&
    typeof archivedRaw === 'object' &&
    typeof (archivedRaw as { toDate?: unknown }).toDate === 'function'
      ? (archivedRaw as { toDate: () => Date }).toDate()
      : null;
  const aiAnalysis =
    data.aiAnalysis && typeof data.aiAnalysis === 'object'
      ? (data.aiAnalysis as PostAiAnalysis)
      : null;
  const loc = data.location as { latitude?: unknown; longitude?: unknown } | null | undefined;
  const location =
    loc && typeof loc.latitude === 'number' && typeof loc.longitude === 'number'
      ? { latitude: loc.latitude, longitude: loc.longitude }
      : null;
  return {
    id: snap.id,
    authorUid: (data.authorUid as string) ?? '',
    authorNickname: (data.authorNickname as string) ?? '(匿名)',
    title: (data.title as string) ?? '',
    comment: (data.comment as string | null) ?? null,
    visibility: ((data.visibility as PostVisibility) ?? 'public') satisfies PostVisibility,
    createdAt,
    photoPath: (data.photoPath as string | null) ?? null,
    photoThumbPath: (data.photoThumbPath as string | null) ?? null,
    photoThumbUrl: (data.photoThumbUrl as string | null) ?? null,
    photoUrl: (data.photoUrl as string | null) ?? null,
    aiAnalysis,
    extraFields: ((data.extraFields as Record<string, unknown>) ?? {}) as Record<string, unknown>,
    archivedAt,
    location,
    agreeCount: typeof data.agreeCount === 'number' ? data.agreeCount : 0,
    sessionId: typeof data.sessionId === 'string' ? data.sessionId : null,
  };
}

/**
 * バッジに表示する文字列を、指定の postField key (`extraFields[key]`) から取得する。
 * 左ペインの絞り込みボタンも同じ項目の値で作る (バッジと絞り込みは同じ項目を見る)。
 *
 * バッジの対象項目は地区の入力項目 (postFields) から選ぶ。既定 key は投稿タイプ (POST_TYPE_FIELD_KEY)。値の解釈:
 *   - string → そのまま (select 型の保存値)
 *   - 配列 (multiselect) → 先頭の非空文字列 (バッジは 1 チップのため代表値のみ)
 *   - それ以外 (未設定 / 数値 / object) → '' (バッジ非表示)
 */
export function getCardPostBadgeLabel(post: CardPrintPost, fieldPath: string | undefined): string {
  const key = fieldPath || POST_TYPE_FIELD_KEY;
  const v = post.extraFields?.[key];
  if (typeof v === 'string') return v;
  if (Array.isArray(v)) {
    const first = v.find((x) => typeof x === 'string' && x.length > 0);
    return typeof first === 'string' ? first : '';
  }
  return '';
}

/** 投稿群から、指定 key のバッジ値を distinct 抽出 (昇順、色設定のプリポピュレート用)。 */
export function collectCardPostBadgeValues(
  posts: readonly CardPrintPost[],
  fieldPath: string | undefined,
): string[] {
  const key = fieldPath || POST_TYPE_FIELD_KEY;
  const set = new Set<string>();
  for (const p of posts) {
    const v = p.extraFields?.[key];
    if (typeof v === 'string' && v) set.add(v);
    else if (Array.isArray(v)) {
      for (const x of v) if (typeof x === 'string' && x) set.add(x);
    }
  }
  return [...set].sort();
}

export interface CardPostFilter {
  keyword: string;
  /** バッジの対象項目の値で絞り込む ('all' は全件)。 */
  badgeValueFilter: string;
  /** バッジの対象項目 (postField key)。'' はバッジ非表示 = 値の絞り込みをしない。 */
  badgeFieldPath: string;
  aiOnly: boolean;
  /**
   * セッション別絞り込み。
   *   - undefined / null / 'all' → 全件 (絞り込まない)
   *   - それ以外 → 下記 `authorSessionsByUid` の有無で判定方法が変わる。
   */
  sessionId?: string | null;
  /**
   * セッション別絞り込みを「投稿者の所属セッション」で行うための uid → joinedSessionIds map。
   *
   * ユーザー設定でセッションにチェック (membership.joinedSessionIds) した参加者の投稿を、
   * カード印刷のセッションドロップダウンで拾えるようにするための判定材料。
   *   - **指定時**: post の `authorUid` がこの map で `sessionId` を含む投稿のみ一致
   *     (post.sessionId は見ない)。投稿者がそのセッションに所属していれば、投稿時の
   *     現セッション設定に関係なく表示される。
   *   - **未指定時**: 後方互換で `post.sessionId === sessionId` で絞り込む。
   */
  authorSessionsByUid?: Record<string, readonly string[]>;
}

/**
 * 投稿選択パネルのフィルタ適用 (pure helper、test 容易)。
 * 旧 `old2/.../PostSelectionPanel.tsx` の filtered useMemo を移植:
 *   - keyword: title / comment / authorNickname を小文字部分一致
 *   - badgeValueFilter: バッジの対象項目 (badgeFieldPath) の値が一致 ('all' / 対象項目なしは全件)
 *   - aiOnly: aiAnalysis.status === 'done' のみ
 *   - sessionId: 投稿者の所属セッション (authorSessionsByUid) 一致。map 未指定時は後方互換で
 *     post.sessionId 一致 (未指定 / null / 'all' は全件)
 */
export function filterCardPrintPosts(
  posts: readonly CardPrintPost[],
  filter: CardPostFilter,
): CardPrintPost[] {
  const k = filter.keyword.trim().toLowerCase();
  const sessionId = filter.sessionId;
  const filterBySession = sessionId != null && sessionId !== 'all';
  const authorSessions = filter.authorSessionsByUid;
  return posts.filter((p) => {
    if (filterBySession) {
      const match = authorSessions
        ? (authorSessions[p.authorUid] ?? []).includes(sessionId)
        : p.sessionId === sessionId;
      if (!match) return false;
    }
    if (
      filter.badgeFieldPath &&
      filter.badgeValueFilter !== 'all' &&
      getCardPostBadgeLabel(p, filter.badgeFieldPath) !== filter.badgeValueFilter
    ) {
      return false;
    }
    if (filter.aiOnly && p.aiAnalysis?.status !== 'done') return false;
    if (k) {
      const hit =
        p.title.toLowerCase().includes(k) ||
        (p.comment ?? '').toLowerCase().includes(k) ||
        p.authorNickname.toLowerCase().includes(k);
      if (!hit) return false;
    }
    return true;
  });
}

export function useCardPrintPosts(districtId: string | null): CardPrintPostsState {
  const [state, setState] = useState<CardPrintPostsState>({
    status: 'loading',
    items: [],
    error: null,
  });
  useEffect(() => {
    if (!districtId) {
      setState({ status: 'error', items: [], error: new Error('districtId is required') });
      return;
    }
    const q = query(collection(db, 'districts', districtId, 'posts'), orderBy('createdAt', 'desc'));
    const unsub: Unsubscribe = onSnapshot(
      q,
      (snap) => {
        const all = snap.docs.map(mapPost);
        // 印刷対象外フィルタ: hidden_all / archivedAt あり を除外
        const items = all.filter((p) => p.visibility !== 'hidden_all' && p.archivedAt === null);
        setState({ status: 'ready', items, error: null });
      },
      (error) => setState({ status: 'error', items: [], error }),
    );
    return () => unsub();
  }, [districtId]);
  return state;
}
